<template>
  <div class="w-full h-full px-5 pt-[14px] pb-[22px] bg-gray-200/20 rounded-xl">
    <div class="flex items-center gap-x-2.5 mb-2">
      <Icon
        v-if="canShowPlayIcon"
        name="HollowPlay"
        :size="14"
        class="stroke-gray-800"
      />

      <div
        class="font-secondary font-medium text-[15px] leading-[20px] text-gray-800"
      >
        Video
      </div>
    </div>

    <div class="w-full h-px mb-4 bg-gray-200/50"></div>

    <MatchMarkers :markers="match.markers" />

    <!-- Timeline filmstrip (infringing side only) -->
    <template v-if="isInfringing && timeline">
      <div class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-3">
        Matching segment
      </div>

      <div ref="timelineContainer" class="relative h-[62px] rounded-[3px] mb-4">
        <div
          class="flex h-full items-center gap-x-px overflow-hidden rounded-[3px]"
        >
          <!-- Hidden measurement image -->
          <img
            v-if="!timelineFramesReady"
            :src="timeline.frames[0]"
            class="h-[56px] invisible absolute"
            @load="onTimelineFrameLoad"
          />

          <img
            v-for="(frameUrl, index) in distributedTimelineFrames"
            :key="index"
            :src="frameUrl"
            class="h-[56px] shrink-0"
            :style="{ width: timelineFrameWidth + 'px' }"
          />
        </div>

        <!-- Match highlight overlay -->
        <div
          class="absolute top-0 h-full border rounded-[3px]"
          :class="{
            'border-red-500': match.level === ScoreLevel.High,
            'border-yellow-500': match.level === ScoreLevel.Medium,
            'border-green-500': match.level === ScoreLevel.Low,
          }"
          :style="{
            left: highlightLeft + '%',
            width: highlightWidth + '%',
          }"
        >
          <div
            class="absolute inset-0"
            :class="{
              'bg-red-500/20': match.level === ScoreLevel.High,
              'bg-yellow-500/20': match.level === ScoreLevel.Medium,
              'bg-green-500/20': match.level === ScoreLevel.Low,
            }"
          ></div>

          <MatchTag
            :id="number"
            :level="match.level"
            class="absolute -top-2 -left-2"
          />
        </div>
      </div>

      <!-- Bracket connector -->
      <div class="w-full relative mb-1">
        <div
          class="w-px h-4 absolute -top-full"
          :class="{
            'bg-red-300': match.level === ScoreLevel.High,
            'bg-yellow-300': match.level === ScoreLevel.Medium,
            'bg-green-300': match.level === ScoreLevel.Low,
          }"
          :style="{ left: markerPosition + '%' }"
        ></div>

        <div
          class="h-[15px] border border-b-0 rounded-t-[3px]"
          :class="{
            'border-red-300': match.level === ScoreLevel.High,
            'border-yellow-400': match.level === ScoreLevel.Medium,
            'border-green-400': match.level === ScoreLevel.Low,
          }"
        ></div>
      </div>
    </template>

    <!-- Timeline filmstrip (copyright side) -->
    <template v-if="!isInfringing && timeline">
      <div class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-3">
        Matching segment
      </div>

      <div ref="timelineContainer" class="relative h-[62px] rounded-[3px] mb-4">
        <div
          class="flex h-full items-center gap-x-px overflow-hidden rounded-[3px]"
        >
          <!-- Hidden measurement image -->
          <img
            v-if="!timelineFramesReady"
            :src="timeline.frames[0]"
            class="h-[56px] invisible absolute"
            @load="onTimelineFrameLoad"
          />

          <img
            v-for="(frameUrl, index) in distributedTimelineFrames"
            :key="index"
            :src="frameUrl"
            class="h-[56px] shrink-0"
            :style="{ width: timelineFrameWidth + 'px' }"
          />
        </div>

        <!-- Match highlight overlay -->
        <div
          class="absolute top-0 h-full border border-gray-500 rounded-[3px]"
          :style="{
            left: highlightLeft + '%',
            width: highlightWidth + '%',
          }"
        >
          <div class="absolute inset-0 bg-gray-500/20"></div>

          <MatchTag :id="number" class="absolute -top-2 -left-2" />
        </div>
      </div>

      <!-- Bracket connector -->
      <div class="w-full relative mb-1">
        <div
          class="w-px h-4 bg-gray-300 absolute -top-full"
          :style="{ left: markerPosition + '%' }"
        ></div>

        <div
          class="h-[15px] border border-b-0 border-gray-300 rounded-t-[3px]"
        ></div>
      </div>
    </template>

    <div
      v-if="!isInfringing && !timeline"
      class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-3"
    >
      Isolated match
    </div>

    <div
      class="border relative rounded-[3px]"
      :class="{
        'border-gray-500': !match.level,
        'border-red-500': match.level === ScoreLevel.High,
        'border-yellow-500': match.level === ScoreLevel.Medium,
        'border-green-500': match.level === ScoreLevel.Low,
      }"
    >
      <div
        ref="framesContainer"
        class="flex h-[62px] items-center pointer-events-none gap-x-px overflow-hidden"
      >
        <!-- Hidden measurement image -->
        <img
          v-if="!framesReady"
          :src="match.frames[0]"
          class="h-[56px] invisible absolute"
          @load="onFrameLoad"
        />

        <img
          v-for="(frameUrl, index) in distributedFrames"
          :key="index"
          :src="frameUrl"
          class="h-[56px] shrink-0"
          :style="{ width: frameWidth + 'px' }"
        />

        <div
          class="w-full absolute top-0 left-0 h-full"
          :class="{
            'bg-gray-500/20': !match.level,
            'bg-red-500/20': match.level === ScoreLevel.High,
            'bg-yellow-500/20': match.level === ScoreLevel.Medium,
            'bg-green-500/20': match.level === ScoreLevel.Low,
          }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { graphql } from '@/shared/graphql/router'
import { ScoreLevel } from '@/shared/graphql/router/graphql'

export const VideoMatchFragment = graphql(`
  fragment VideoMatchFields on VideoMatch {
    id
    location
    summary
    level
    frames
    start
    end
    markers {
      color
      shine
      visible
    }
  }
`)
</script>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import MatchMarkers from './MatchMarkers.vue'
import MatchTag from './matches/MatchTag.vue'
import { Icon } from '@dacr/ui'
import { type ComputedRef, computed, inject, ref, watch } from 'vue'

const FRAME_HEIGHT = 56
const FRAME_GAP = 1

type Timeline = { frames: string[]; duration: number }

const props = defineProps<{
  match: FragmentType<typeof VideoMatchFragment>
  number: number
  side?: 'registered' | 'infringing'
  allowPlayback?: boolean
}>()

const match = useFragment(VideoMatchFragment, props.match)

const canShowPlayIcon = computed(() => props.allowPlayback !== false)

const timeline = inject<ComputedRef<Timeline | undefined>>('timeline')
const copyrightDuration =
  inject<ComputedRef<number | undefined>>('copyrightDuration')

const isInfringing = computed(() => !!match.summary)

const duration = computed(() =>
  isInfringing.value ? timeline?.value?.duration : copyrightDuration?.value
)

// --- Match frames distribution ---

const framesContainer = ref<HTMLElement>()
const containerWidth = ref(0)
const frameWidth = ref(0)

const onFrameLoad = (event: Event) => {
  const img = event.target as HTMLImageElement
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    frameWidth.value = Math.round(
      (FRAME_HEIGHT / img.naturalHeight) * img.naturalWidth
    )
  }
}

const framesReady = computed(
  () => containerWidth.value > 0 && frameWidth.value > 0
)

watch(framesContainer, (el, _, onCleanup) => {
  if (!el) {
    containerWidth.value = 0
    return
  }
  const observer = new ResizeObserver(([entry]) => {
    containerWidth.value = entry.contentRect.width
  })
  observer.observe(el)
  onCleanup(() => observer.disconnect())
})

const distributedFrames = computed(() => {
  if (!framesReady.value) return []
  const frames = match.frames
  if (frames.length <= 2) return frames

  const maxFrames = Math.max(
    2,
    Math.ceil(
      (containerWidth.value - frameWidth.value) /
        (frameWidth.value + FRAME_GAP) +
        1
    )
  )

  if (frames.length <= maxFrames) return frames

  const result: string[] = [frames[0]]
  const innerCount = maxFrames - 2
  for (let i = 1; i <= innerCount; i++) {
    const index = Math.round((i * (frames.length - 1)) / (maxFrames - 1))
    result.push(frames[index])
  }
  result.push(frames[frames.length - 1])

  return result
})

// --- Timeline frames distribution ---

const timelineContainer = ref<HTMLElement>()
const timelineContainerWidth = ref(0)
const timelineFrameWidth = ref(0)

const onTimelineFrameLoad = (event: Event) => {
  const img = event.target as HTMLImageElement
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    timelineFrameWidth.value = Math.round(
      (FRAME_HEIGHT / img.naturalHeight) * img.naturalWidth
    )
  }
}

const timelineFramesReady = computed(
  () => timelineContainerWidth.value > 0 && timelineFrameWidth.value > 0
)

watch(timelineContainer, (el, _, onCleanup) => {
  if (!el) {
    timelineContainerWidth.value = 0
    return
  }
  const observer = new ResizeObserver(([entry]) => {
    timelineContainerWidth.value = entry.contentRect.width
  })
  observer.observe(el)
  onCleanup(() => observer.disconnect())
})

const distributedTimelineFrames = computed(() => {
  if (!timelineFramesReady.value || !timeline?.value) return []
  const frames = timeline.value.frames
  if (frames.length <= 2) return frames

  const maxFrames = Math.max(
    2,
    Math.ceil(
      (timelineContainerWidth.value - timelineFrameWidth.value) /
        (timelineFrameWidth.value + FRAME_GAP) +
        1
    )
  )

  if (frames.length <= maxFrames) return frames

  const result: string[] = [frames[0]]
  const innerCount = maxFrames - 2
  for (let i = 1; i <= innerCount; i++) {
    const index = Math.round((i * (frames.length - 1)) / (maxFrames - 1))
    result.push(frames[index])
  }
  result.push(frames[frames.length - 1])

  return result
})

// --- Match highlight position ---

const markerPosition = computed(() => {
  if (!duration.value) return 0
  return ((Number(match.start) + Number(match.end)) / 2 / duration.value) * 100
})

const highlightLeft = computed(() => {
  if (!duration.value) return 0
  return (Number(match.start) / duration.value) * 100
})

const highlightWidth = computed(() => {
  if (!duration.value) return 0
  return ((Number(match.end) - Number(match.start)) / duration.value) * 100
})
</script>
